import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ApiClient } from "./ApiClient";
import { handleUnauthorized } from "../utils/auth/authHandler";

const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;

/**
 * Custom base query that mirrors ApiClient logic for RTK Query
 */
const baseQueryWithAuth = async (args, api, extraOptions) => {
  // 1. Get the CSRF token. Passing 'true' allows it to return null
  // if the session is dead, rather than throwing an error.
  const csrfToken = await ApiClient.getCsrfToken(true);

  if (csrfToken === null) {
    // Skip auth redirect if endpoint requests it
    if (!extraOptions?.skipAuthRedirect) {
      handleUnauthorized();
    }
    return {
      error: { status: 401, data: { message: "Session expired" } },
    };
  }

  // 2. Execute the actual request using RTK's fetchBaseQuery
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${apiBaseUrl}/account`,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("X-CSRFToken", csrfToken);
      // RTK Query usually sets Content-Type: application/json by default
      return headers;
    },
  });

  const result = await rawBaseQuery(args, api, extraOptions);

  // 3. Centralized 401 Check for the response
  if (result.error?.status === 401 && !extraOptions?.skipAuthRedirect) {
    handleUnauthorized();
  }

  return result;
};

export const accountApi = createApi({
  reducerPath: "accountApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["account-info", "UNAUTHORIZED", "passkeys"],
  endpoints: (builder) => ({
    getAccount: builder.query({
      query: () => ({ url: `/get-account` }),
      transformResponse: (response) => response.account,
      transformErrorResponse: (response) => response.status,
      providesTags: ["account-info", "UNAUTHORIZED"],
    }),

    updateAccountSettings: builder.mutation({
      query: ({ setting, values }) => ({
        url: "/account-settings",
        method: "PUT",
        body: { setting, values },
      }),
      transformErrorResponse: (response) => response.status,
      invalidatesTags: ["account-info"],
    }),

    updatePreferences: builder.mutation({
      query: ({ section, preferences }) => ({
        url: "/update-preferences",
        method: "PUT",
        body: { section, preferences },
      }),
      transformErrorResponse: (response) => response.status,
      invalidatesTags: ["account-info"],

      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          const currentState = getState();
          const currentUser = currentState.auth.user;

          if (currentUser) {
            const updatedUser = {
              ...currentUser,
              preferences: {
                ...currentUser.preferences,
                [arg.section]: arg.preferences,
              },
            };
            dispatch({ type: "auth/loginRdx", payload: updatedUser });
          }
        } catch (error) {
          console.error("Failed to update preferences:", error);
        }
      },
    }),

    requestAccountDeletionCode: builder.mutation({
      query: () => ({
        url: "/request-account-deletion",
        method: "POST",
      }),
    }),

    deleteAccount: builder.mutation({
      query: (data) => ({
        url: "/delete-account",
        method: "DELETE",
        body: data,
      }),
      extraOptions: { skipAuthRedirect: true },
      // Note: Do NOT invalidate tags here so RTK query doesn't try to auto-refetch
      // getAccount after the user session has been deleted!
    }),

    // Passkey endpoints live under /api/auth/passkeys, not /api/account.
    listPasskeys: builder.query({
      query: () => ({ url: `${apiBaseUrl}/auth/passkeys` }),
      transformErrorResponse: (response) => response.status,
      providesTags: ["passkeys"],
    }),

    // Two-step MFA flow: without mfa_code the backend responds 400
    // MFA_REQUIRED; call again with the code to get real options.
    getPasskeyRegistrationOptions: builder.mutation({
      query: (mfa_code) => ({
        url: `${apiBaseUrl}/auth/passkeys/registration/options`,
        method: "POST",
        body: mfa_code ? { mfa_code } : {},
      }),
    }),

    verifyPasskeyRegistration: builder.mutation({
      query: ({ credential, friendly_name }) => ({
        url: `${apiBaseUrl}/auth/passkeys/registration/verify`,
        method: "POST",
        body: { credential, friendly_name },
      }),
      invalidatesTags: ["passkeys"],
    }),

    // Two-step MFA flow, same as registration options above.
    revokePasskey: builder.mutation({
      query: ({ id, mfa_code }) => ({
        url: `${apiBaseUrl}/auth/passkeys/${id}`,
        method: "DELETE",
        body: mfa_code ? { mfa_code } : {},
      }),
      invalidatesTags: ["passkeys"],
    }),
  }),
});

export const {
  useUpdateAccountSettingsMutation,
  useGetAccountQuery,
  useUpdatePreferencesMutation,
  useRequestAccountDeletionCodeMutation,
  useDeleteAccountMutation,
  useListPasskeysQuery,
  useGetPasskeyRegistrationOptionsMutation,
  useVerifyPasskeyRegistrationMutation,
  useRevokePasskeyMutation,
} = accountApi;
